// Popup.jsx

import React from 'react';

// React DOM
import ReactDOM from 'react-dom';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Context
import { useTheme } from 'context/ThemeContext';

// Hooks
import usePopup from 'components/popup/usePopup';

// CSS
import './Popup.css';

/**
 * Popup
 *
 * Renders a popup component positioned relative to an anchor element, with responsive 
 * behavior for mobile devices.
 * 
 * @param {object} anchorRef - Ref to the anchor element for positioning the popup.
 * @param {boolean} isOpen - Optional controlled open state of the popup.
 * @param {function} setOpen - Optional controlled setter for the popup's open state.
 * @param {ReactNode} children - Content to render inside the popup.
 *
 * @returns {ReactNode} - Popup.
 */
const Popup = React.memo(({
    anchorRef,
    anchorPoint,
    isOpen,
    setOpen,
    children,
    align = 'auto',
    placement = 'auto',
    offset = 2,
    viewportPadding = 8,
    zIndex,
}) => {

    // ----- CONTEXT ----- //

    const { theme } = useTheme();

    // ----- DEVICE DETECTION ----- //

    const isMobile = useIsMobile();

    // ----- POPUP HOOK ----- //

    const {
        popupRef,
        isOpen: popupIsOpen,
        isVisible,
        position,
    } = usePopup({
        align,
        placement,
        offset,
        viewportPadding,
        ...(anchorRef ? { anchorRef } : {}),
        ...(anchorPoint ? { anchorPoint } : {}),
        ...(typeof isOpen === 'boolean' ? { isOpen } : {}),
        ...(typeof setOpen === 'function' ? { setOpen } : {}),
    });

    const resolveAutoZIndex = React.useCallback(() => {
        if (zIndex !== undefined) return zIndex;
        if (typeof window === 'undefined') return undefined;
        const anchorElement = anchorRef?.current;
        if (!anchorElement || typeof anchorElement.closest !== 'function') return undefined;

        // If popup is anchored inside a modal, lift it above that modal's content layer.
        const modalElement = anchorElement.closest('.modal-content');
        if (!modalElement) return undefined;

        const modalZ = Number.parseInt(window.getComputedStyle(modalElement).zIndex || '', 10);
        if (!Number.isFinite(modalZ)) return undefined;
        return modalZ + 1;
    }, [zIndex, anchorRef]);

    const resolvedZIndex = resolveAutoZIndex();

    // Surface Style for Desktop (positioned) vs Mobile (static)
    const surfaceStyle = {
        ...(isMobile
            ? {
                backgroundColor: theme.popup,
            }
            : {
                backgroundColor: theme.popup,
                top: position.top,
                left: position.left,
            }),
        ...(resolvedZIndex !== undefined ? { zIndex: resolvedZIndex } : {}),
    };

    // Portal Root for rendering the popup outside the normal DOM hierarchy
    const portalRoot = document.getElementById('portal-root') || document.body;

    // Only render the popup surface when it's visible to avoid unnecessary DOM elements
    const surface = isVisible ? (
        <div ref={popupRef}
            className={`popup-container ${isMobile ? 'mobile' : 'desktop'} ${popupIsOpen ? 'open' : 'closed'}`}
            style={surfaceStyle}
        >
            {children}
        </div>
    ) : null;

    // ----- RENDER ----- //

    return (
        <div className="popup-root">
            {surface ? ReactDOM.createPortal(surface, portalRoot) : null}
        </div>
    );
});

// Dev Tools
Popup.displayName = 'Popup';

export default Popup;
